import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import apiService from '../../services/api.service';

interface User {
    userid: number;
    username: string;
    email: string;
    fullname: string;
    department?: string;
    role: string;
    roles?: string[];
    // Optional per-user modules for access control
    allowedmodules?: string[];
    allowedprojectcodes?: string[];
    allowedcostcenters?: string[];
}

interface AuthState {
    user: User | null;
    token: string | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    error: string | null;
}

const initialState: AuthState = {
    user: null,
    token: localStorage.getItem('token'),
    isAuthenticated: !!localStorage.getItem('token'),
    isLoading: false,
    error: null,
};

interface LoginResult {
    user: User | null;
    token: string;
    requirePasswordChange?: boolean;
    passwordChangeData?: { userId: number; username: string };
}

type AuthRequestError = {
    message: string;
    status?: number;
};

// Async thunks
export const login = createAsyncThunk(
    'auth/login',
    async (
        { username, password }: { username: string; password: string },
        { rejectWithValue }
    ) => {
        try {
            const res = await apiService.login(username, password);
            if (!res?.success) {
                return rejectWithValue(res?.message || 'Login failed');
            }

            if (res?.requirePasswordChange) {
                return {
                    user: null,
                    token: '',
                    requirePasswordChange: true,
                    passwordChangeData: res.data
                } as LoginResult;
            }

            if (!res?.data?.token) {
                return rejectWithValue(res?.message || 'Login failed');
            }
            const { token, user } = res.data;
            localStorage.setItem('token', token);
            return { user, token };
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Login failed');
        }
    }
);

export const getProfile = createAsyncThunk(
    'auth/getProfile',
    async (_, { rejectWithValue }) => {
        try {
            const res = await apiService.getProfile();
            if (!res?.success || !res?.data) {
                return rejectWithValue(res?.message || 'Failed to get profile');
            }
            return res.data;
        } catch (error: any) {
            return rejectWithValue({
                message: error.response?.data?.message || error.message || 'Failed to get profile',
                status: error.response?.status,
            } satisfies AuthRequestError);
        }
    }
);

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        logout: (state) => {
            state.user = null;
            state.token = null;
            state.isAuthenticated = false;
            localStorage.removeItem('token');
        },
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        // Login
        builder.addCase(login.pending, (state) => {
            state.isLoading = true;
            state.error = null;
        });
        builder.addCase(login.fulfilled, (state, action: PayloadAction<any>) => {
            state.isLoading = false;
            if (action.payload.requirePasswordChange) {
                state.user = null;
                state.token = null;
                state.isAuthenticated = false;
            } else {
                state.user = action.payload.user as User;
                state.token = action.payload.token;
                state.isAuthenticated = true;
            }
        });
        builder.addCase(login.rejected, (state, action: PayloadAction<any>) => {
            state.isLoading = false;
            state.error = action.payload;
        });

        // Get Profile
        builder.addCase(getProfile.pending, (state) => {
            state.isLoading = true;
        });
        builder.addCase(getProfile.fulfilled, (state, action: PayloadAction<User>) => {
            state.isLoading = false;
            state.user = action.payload;
        });
        builder.addCase(getProfile.rejected, (state, action) => {
            state.isLoading = false;
            const error = action.payload as AuthRequestError | undefined;
            // Network/5xx failures must not log a user out. Only an explicit
            // unauthorized response means the token is no longer valid.
            if (error?.status === 401) {
                state.isAuthenticated = false;
                state.token = null;
                localStorage.removeItem('token');
            }
            state.error = error?.message || 'Failed to get profile';
        });
    },
});

export const { logout, clearError } = authSlice.actions;
export default authSlice.reducer;
